/**
 * What the run is doing, line by line — its own tab, looking like a terminal.
 *
 * The canvas says *which* node is working; this says what it is doing inside
 * it — "staging 42 of 200 · protocol-a.pdf" — which is the only thing that
 * moves during the two minutes an export takes. Written by the engine on its
 * own database connection, so a line appears while the node is still mid-flight
 * rather than all at once at the end.
 *
 * Deliberately a terminal and not a table: a log is read by scanning down it,
 * and columns invite sorting and filtering that nobody wants while they are
 * waiting for something to finish.
 */

import { useEffect, useMemo, useRef } from "react";
import { Badge, Box, Group, ScrollArea, Stack, Text } from "@mantine/core";

export interface RunLogLine {
  at: string;
  node_id?: string | null;
  text: string;
}

interface Props {
  lines: RunLogLine[];
  /** Node id → label, so a line names the node the way the canvas does. */
  labelOf: (nodeId: string) => string;
  /** True while the run is in flight — drives the live badge and autoscroll. */
  live: boolean;
  /** For the header line: which run this is, if any. */
  startedAt?: string | null;
}

function clockOf(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? ""
    : at.toLocaleTimeString(undefined, { hour12: false, timeStyle: "medium" });
}

export function RunLog({ lines, labelOf, live, startedAt }: Props) {
  const viewport = useRef<HTMLDivElement>(null);

  // Follow the tail while it runs. Once it has finished, leave the scroll where
  // the reader put it — they are reading now, not watching.
  useEffect(() => {
    if (live && viewport.current) {
      // Assigning scrollTop rather than calling scrollTo: the same result, and
      // it does not depend on an API jsdom leaves out, so this stays testable.
      viewport.current.scrollTop = viewport.current.scrollHeight;
    }
  }, [live, lines.length]);

  const rendered = useMemo(
    () =>
      lines.map((line, i) => ({
        key: `${line.at}-${i}`,
        clock: clockOf(line.at),
        node: line.node_id ? labelOf(line.node_id) : "",
        text: line.text,
      })),
    [lines, labelOf],
  );

  return (
    <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
      <Group gap="xs" align="center">
        {live ? (
          <Badge size="sm" color="yellow" variant="light">
            live
          </Badge>
        ) : (
          <Badge size="sm" color="gray" variant="light">
            idle
          </Badge>
        )}
        <Text size="xs" c="dimmed">
          {lines.length === 0
            ? "Waiting for a run."
            : `${lines.length} lines${startedAt ? ` · run started ${clockOf(startedAt)}` : ""}`}
        </Text>
      </Group>

      <Box
        style={{
          flex: 1,
          minHeight: 0,
          borderRadius: 8,
          border: "1px solid var(--mantine-color-default-border)",
          // A terminal reads as a terminal: dark ground, monospace, nothing
          // else competing for attention.
          background: "var(--mantine-color-dark-8)",
          color: "var(--mantine-color-gray-3)",
        }}
      >
        <ScrollArea viewportRef={viewport} type="auto" scrollbarSize={8} style={{ height: "100%" }}>
          <Box
            p="sm"
            style={{ fontFamily: "var(--mantine-font-family-monospace)", fontSize: 12.5 }}
          >
            {rendered.length === 0 ? (
              <Text size="xs" c="dimmed" ff="monospace">
                Nothing yet. Run the pipeline and it will say what it is doing.
              </Text>
            ) : (
              rendered.map((l) => (
                <div key={l.key} style={{ display: "flex", gap: 10, lineHeight: 1.75 }}>
                  <span style={{ color: "var(--mantine-color-dark-2)", flex: "0 0 auto" }}>
                    {l.clock}
                  </span>
                  {l.node && (
                    <span style={{ color: "var(--mantine-color-blue-4)", flex: "0 0 auto" }}>
                      {l.node}
                    </span>
                  )}
                  <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{l.text}</span>
                </div>
              ))
            )}
            {live && (
              <Text component="span" c="yellow.5" ff="monospace" size="xs">
                ▋
              </Text>
            )}
          </Box>
        </ScrollArea>
      </Box>
    </Stack>
  );
}
